import React, { useEffect, useState } from 'react';
import {
  PrinterIcon,
  CheckCircleIcon,
  StopIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

/**
 * ActionButtons Component
 * Main action buttons for order submission, printing, and parking
 */
export const ActionButtons = ({
  itemCount,
  total,
  paymentTotalOverride,
  allowEmptyCartPayment,
  selectedTableId,
  currentOrderId,
  onSendOrder,
  onSubmitOrder,
  onPrintBill,
  onBillOut,
  onPrintKitchen,
  onPrintBar,
  onParkOrder,
  openPaymentModalToken,
  loading,
  error,
  transactionType
}) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [tendered, setTendered] = useState('0');
  const effectiveTotal = Number(
    allowEmptyCartPayment && Number.isFinite(Number(paymentTotalOverride))
      ? paymentTotalOverride
      : total
  );
  const changeDue = parseFloat(tendered) - effectiveTotal;

  useEffect(() => {
    if (!openPaymentModalToken) return;
    setShowPaymentModal(true);
  }, [openPaymentModalToken]);

  const handleSubmit = async () => {
    if (!allowEmptyCartPayment && itemCount === 0) {
      alert('Cart is empty');
      return;
    }

    try {
      await onSubmitOrder(paymentMethod, parseFloat(tendered) || 0);
      setShowPaymentModal(false);
      setTendered('0');
      setPaymentMethod('cash');
    } catch (err) {
      console.error('Submit error:', err);
    }
  };

  const handleSendOrder = async () => {
    console.log('[DEBUG] ActionButtons handleSendOrder called');
    console.log('[DEBUG] onSubmitOrder prop:', typeof onSubmitOrder);

    if (itemCount === 0) {
      alert('Cart is empty');
      return;
    }

    try {
      await onSendOrder?.();
    } catch (err) {
      console.error('Send order error:', err);
    }
  };

  const isDisabled = itemCount === 0 || loading;

  return (
    <div className="p-4 bg-white border-t border-gray-200 space-y-2">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Primary Action Buttons */}
      <div className="grid grid-cols-1 gap-2">
        {/* Submit Order */}
        <button
          onClick={handleSendOrder}
          disabled={isDisabled}
          className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition ${
            isDisabled
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          <CheckCircleIcon className="h-5 w-5" />
          <span>Submit Order ({itemCount})</span>
        </button>
      </div>

      {/* Secondary Action Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onPrintBill?.(selectedTableId)}
          disabled={loading || !selectedTableId}
          className={`py-2 rounded-lg font-medium text-sm transition ${
            loading || !selectedTableId
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
          }`}
        >
          Print Bill
        </button>

        <button
          onClick={() => onBillOut?.(selectedTableId)}
          disabled={loading || !selectedTableId}
          className={`py-2 rounded-lg font-medium text-sm transition ${
            loading || !selectedTableId
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
          }`}
        >
          Bill Out
        </button>
      </div>

      {/* Kitchen/Bar Print Buttons */}
      <div className="grid grid-cols-2 gap-2">
        {/* Print Kitchen */}
        <button
          onClick={() => onPrintKitchen(currentOrderId)}
          disabled={isDisabled || !currentOrderId}
          title={!currentOrderId ? 'Create an order first' : 'Print kitchen tickets'}
          className={`py-2 rounded-lg font-medium flex items-center justify-center space-x-1 text-sm transition ${
            (isDisabled || !currentOrderId)
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
          }`}
        >
          <PrinterIcon className="h-4 w-4" />
          <span>Kitchen</span>
        </button>

        {/* Print Bar */}
        <button
          onClick={() => onPrintBar(currentOrderId)}
          disabled={isDisabled || !currentOrderId}
          title={!currentOrderId ? 'Create an order first' : 'Print bar tickets'}
          className={`py-2 rounded-lg font-medium flex items-center justify-center space-x-1 text-sm transition ${
            (isDisabled || !currentOrderId)
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-purple-100 hover:bg-purple-200 text-purple-700'
          }`}
        >
          <PrinterIcon className="h-4 w-4" />
          <span>Bar</span>
        </button>
      </div>

      {/* Tertiary Action Buttons */}
      <div className="grid grid-cols-1 gap-2">
        {/* Park Order */}
        <button
          onClick={onParkOrder}
          disabled={isDisabled}
          className={`py-2 rounded-lg font-medium flex items-center justify-center space-x-2 text-sm transition ${
            isDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-orange-100 hover:bg-orange-200 text-orange-700'
          }`}
        >
          <StopIcon className="h-4 w-4" />
          <span>Park Order</span>
        </button>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-2xl font-bold mb-6">Complete Order</h3>

            {/* Order Total */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="text-center">
                <p className="text-gray-600 text-sm">Total Amount</p>
                <p className="text-3xl font-bold text-gray-900">₱{effectiveTotal.toFixed(2)}</p>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('cash')}
                  className={`py-3 rounded-lg font-medium transition ${
                    paymentMethod === 'cash'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cash
                </button>
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`py-3 rounded-lg font-medium transition ${
                    paymentMethod === 'card'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Card
                </button>
              </div>
            </div>

            {/* Amount Tendered (Cash Only) */}
            {paymentMethod === 'cash' && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount Tendered
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tendered}
                  onChange={(e) => setTendered(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:outline-none focus:border-blue-500"
                />
                {changeDue !== 0 && (
                  <div className="mt-2 text-right">
                    <p className="text-sm text-gray-600">Change Due:</p>
                    <p
                      className={`text-xl font-bold ${
                        changeDue >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      ₱{changeDue.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Transaction Type Info */}
            {transactionType && (
              <div className="mb-6 bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Type:</span>{' '}
                  {transactionType.charAt(0).toUpperCase() + transactionType.slice(1)}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  loading ||
                  (paymentMethod === 'cash' && changeDue < 0)
                }
                className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition ${
                  loading || (paymentMethod === 'cash' && changeDue < 0)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {loading ? 'Processing...' : 'Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionButtons;
